import { keysOf } from '@utils/keysOf';

import { Typegen0 as LoanApplicationMachineTypes } from '../LoanApplication.machine.typegen';

export type { LoanApplication as LoanApplicationData } from '@shared/api/@types/loans';

export enum EmploymentStatus {
  FullTime = 'full_time',
  PartTime = 'part_time',
  Casual = 'casual',
  SelfEmployed = 'self_employed',
  Unemployed = 'unemployed',
  Retired = 'retired',
}

export enum RelationshipStatus {
  SingleOrNeverMarried = 'single_or_never_married',
  Widowed = 'widowed',
  Divorced = 'divorced',
  Separated = 'separated',
  MarriedOrDeFacto = 'married_or_de_facto',
}

export const EmploymentStatusLabels: Record<EmploymentStatus, string> = {
  [EmploymentStatus.FullTime]: 'Full time',
  [EmploymentStatus.PartTime]: 'Part time',
  [EmploymentStatus.Casual]: 'Casual',
  [EmploymentStatus.SelfEmployed]: 'Self employed',
  [EmploymentStatus.Unemployed]: 'Not employed',
  [EmploymentStatus.Retired]: 'Retired',
};

export const RelationshipStatusLabels: Record<RelationshipStatus, string> = {
  [RelationshipStatus.SingleOrNeverMarried]: 'Single or never married',
  [RelationshipStatus.Widowed]: 'Widowed',
  [RelationshipStatus.Divorced]: 'Divorced',
  [RelationshipStatus.Separated]: 'Separated',
  [RelationshipStatus.MarriedOrDeFacto]: 'Married or de-facto',
};

export type MobileCodeLoanApplicationData = {
  userInputCode: string;
  error: string;
};

export type LoanApplicationStepId = Extract<LoanApplicationMachineTypes['matchesStates'], string>;

// Only include 2 levels deep
export type UpdateProfileSteps = Exclude<
  Extract<LoanApplicationStepId, `setupProfile.${string}`>,
  `setupProfile.${string}.${string}`
>;

// creates a compile error if a new `setupProfile.*` step is added so we know to update this
const UpdateProfileStepLookup: Record<UpdateProfileSteps, null> = {
  'setupProfile.nameAndDob': null,
  'setupProfile.address': null,
  'setupProfile.investingPlans': null,
};

export const UpdateProfileStepIds = keysOf(UpdateProfileStepLookup);

// Returns only the ids of the root steps
// - use as `LoanApplicationRootSteps` and ignore the T generic type
// - at time of writing the optional `T` generic is required to make the type work :sadge:
export type LoanApplicationRootSteps<T = LoanApplicationStepId> = T extends Extract<
  LoanApplicationStepId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  `${infer Root}.${infer Child}`
>
  ? Root
  : never;

// Given a root id, returns all the child steps without the root included
// - use as `ExtractLoanApplicationChildSteps<'root_id'>` and ignore the T generic type
// - at time of writing the optional `T` generic is required to make the type work :sadge:
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ExtractLoanApplicationChildSteps<
  Root extends LoanApplicationRootSteps | `${LoanApplicationRootSteps}.${string}`,
  T = LoanApplicationStepId,
> = T extends Extract<LoanApplicationStepId, `${Root}.${infer Child}`> ? Child : never;
